'use client';

import {
  getWebInstrumentations,
  initializeFaro,
  isInternalFaroOnGlobalObject,
  LogLevel,
  TransportItemType,
  type LogEvent,
  type TransportItem,
} from '@grafana/faro-web-sdk';

import { normalizeUrl } from '@/lib/otel/normalizePath';

/**
 * URLs excluded from Faro fetch/XHR tracing and performance resource events.
 * Prevents large multipart uploads from bloating telemetry batches (Grafana 413).
 */
const FARO_IGNORE_URL_PATTERNS: RegExp[] = [
  /\/api\/upload-image(?:\?|$|\/)/,
];

/** Smaller than SDK default (50) to stay under Grafana collector body limits. */
const FARO_BATCHING = {
  enabled: true,
  itemLimit: 20,
  sendTimeout: 500,
} as const;

/** Faro log lines below this are not sent. Events have no level field. */
const KEPT_FARO_LOG_LEVELS = new Set<LogLevel>([LogLevel.WARN, LogLevel.ERROR]);

/**
 * Keep warn and error logs only, then templatize the page URL so the rest
 * don't produce one series per id. See {@link normalizeUrl}.
 */
export function prepareFaroItem(item: TransportItem): TransportItem | null {
  if (item.type === TransportItemType.LOG) {
    const level = (item.payload as LogEvent).level;
    if (!KEPT_FARO_LOG_LEVELS.has(level)) return null;
  }

  const url = item.meta?.page?.url;
  if (url && item.meta?.page) {
    item.meta.page.url = normalizeUrl(url);
  }
  return item;
}

export function initFaroClient(): void {
  if (process.env.NODE_ENV !== 'production') {
    return;
  }

  if (typeof window === 'undefined' || isInternalFaroOnGlobalObject()) {
    return;
  }

  const url = process.env.NEXT_PUBLIC_FARO_URL?.trim();
  if (!url) {
    return;
  }

  try {
    initializeFaro({
      url,
      app: {
        name: 'vupt-market',
        version: '1.0.0',
        environment: process.env.NEXT_PUBLIC_FARO_ENVIRONMENT || 'development',
        ...(process.env.NEXT_PUBLIC_FARO_BUNDLE_ID
          ? { bundleId: process.env.NEXT_PUBLIC_FARO_BUNDLE_ID }
          : {}),
      },
      batching: FARO_BATCHING,
      ignoreUrls: FARO_IGNORE_URL_PATTERNS,
      // Default tracks every fetch/XHR as faro.performance.resource. false skips that event.
      trackResources: false,
      beforeSend: prepareFaroItem,
      instrumentations: [
        ...getWebInstrumentations({
          captureConsole: false,
        }),
      ],
      ignoreErrors: [
        /^ResizeObserver loop limit exceeded$/,
        /^ResizeObserver loop completed with undelivered notifications$/,
        /^Script error\.$/,
        /chrome-extension:\/\//,
        /moz-extension:\/\//,
        /@grafana\/faro-web-sdk:transport-fetch/,
        /Failed sending payload to the receiver/,
        /TimeoutError/,
        /signal timed out/,
      ],
    });
  } catch (error) {
    console.error('[Faro] initializeFaro failed:', error);
  }
}
